<template>
  <UIModal
    id="bulk-edit-weekly-hours-modal"
    v-model:show="internalShow"
    :mask-closable="false"
    preset="card"
    class="w-[500px] max-h-[90vh]"
  >
    <template #header>
      <div class="flex w-full items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-900">
          {{ $t('settings.availability.schedule.update_weekly_hours_for_users', { count: props.users.length }) }}
        </h3>
        <XCloseIcon
          class="h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-700"
          role="button"
          tabindex="0"
          :aria-label="$t('aria_labels.close_modal')"
          @click="handleCancel"
          @keydown.enter="handleCancel"
          @keydown.space.prevent="handleCancel"
        />
      </div>
    </template>
    <UIModalContent class="max-h-[calc(90vh-140px)] overflow-y-auto">
      <div class="flex flex-col gap-4">
        <!-- Assign to Section (Non-editable users) -->
        <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <span class="text-sm font-medium text-gray-700 mb-2 block">
            {{ $t('settings.availability.schedule.selected_users') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <!-- Visible user chips (up to 6, or all if expanded) -->
            <div
              v-for="user in visibleUsers"
              :key="user.id"
              class="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 pl-1 pr-3 py-1 max-w-[180px]"
              :title="user.name"
            >
              <div
                class="flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
              >
                <UIAvatar
                  v-if="user.profilePhoto"
                  :src="user.profilePhoto"
                  :alt="user.name"
                  class="h-6 w-6 text-[10px]"
                />
                <span v-else class="text-[10px] font-medium text-gray-600">
                  {{ getInitials(user.name) }}
                </span>
              </div>
              <span class="text-sm text-gray-700 truncate">{{ user.name }}</span>
            </div>
            <!-- "+X more" chip with chevron when there are more than 6 users -->
            <div
              v-if="hasMoreUsers"
              class="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-3 py-1 cursor-pointer hover:bg-gray-50 transition-colors"
              role="button"
              tabindex="0"
              @click="isUserListExpanded = !isUserListExpanded"
              @keydown.enter="isUserListExpanded = !isUserListExpanded"
            >
              <span class="text-sm text-gray-700">
                {{ isUserListExpanded ? t('settings.availability.schedule.show_less') : `+${remainingUsersCount} ${t('settings.availability.schedule.more')}` }}
              </span>
              <component
                :is="isUserListExpanded ? ChevronUpIcon : ChevronDownIcon"
                class="h-4 w-4 text-gray-500"
              />
            </div>
          </div>
        </div>

        <!-- Timezone Selector -->
        <UIFormItem
          class="w-full"
          :label="$t('settings.availability.schedule.timezone')"
          :show-feedback="false"
        >
          <UISelect
            id="bulk-edit-timezone-select"
            v-model:value="selectedTimezone"
            class="w-full px-1"
            :options="computedTimezones"
            :filterable="true"
            :placeholder="$t('settings.availability.schedule.timezone_placeholder')"
          />
        </UIFormItem>

        <!-- Weekly Schedule Editor -->
        <div class="rounded-lg border border-gray-200 overflow-hidden">
          <div
            v-for="(day, index) in weeklySchedule"
            :key="index"
            class="px-4 py-3"
            :class="{ 
              'border-b border-gray-100': index !== weeklySchedule.length - 1,
              'items-center': !day.isActive
            }"
          >
            <div class="flex flex-row">
              <!-- Day Checkbox -->
              <UICheckbox
                :id="`bulk-${day.day}`"
                class="w-16 min-w-[75px] flex-shrink-0"
                :class="{ 'mt-7': day.isActive && day.intervals.length > 0 }"
                :name="day.day"
                :checked="day.isActive"
                @change="toggleDayEnabled(day)"
              >
                {{ capitalizeAbbr(day.day || '') }}
              </UICheckbox>

              <!-- Time Slots -->
              <div v-if="day.isActive && day.intervals.length > 0" class="flex-1">
                <div
                  v-for="(slot, intervalIndex) in day.intervals"
                  :key="intervalIndex"
                  class="flex items-end gap-3 mb-2 last:mb-0"
                >
                  <!-- Start Time -->
                  <div class="flex flex-col gap-1 w-[140px]">
                    <span v-if="intervalIndex === 0" class="text-xs font-medium text-gray-600">
                      {{ $t('settings.availability.schedule.start_time') }}
                    </span>
                    <UITimePicker
                      v-model:formatted-value="slot.from"
                      class="w-full"
                      value-format="H:m"
                      format="hh:mm a"
                      :minutes="5"
                      use-12-hours
                      :actions="['confirm']"
                    />
                  </div>
                  
                  <!-- End Time -->
                  <div class="flex flex-col gap-1 w-[140px]">
                    <span v-if="intervalIndex === 0" class="text-xs font-medium text-gray-600">
                      {{ $t('settings.availability.schedule.end_time') }}
                    </span>
                    <UITimePicker
                      v-model:formatted-value="slot.to"
                      class="w-full"
                      value-format="H:m"
                      format="hh:mm a"
                      :minutes="5"
                      use-12-hours
                      :actions="['confirm']"
                    />
                  </div>

                  <!-- Action Buttons -->
                  <div 
                    class="flex gap-2 flex-shrink-0"
                    :class="{ 'mt-4 pt-2': intervalIndex === 0 }"
                  >
                    <!-- Add slot button (only on last row) -->
                    <UIButton
                      v-if="intervalIndex === day.intervals.length - 1"
                      :text="true"
                      :aria-label="$t('settings.availability.schedule.add_availability_hour')"
                      class="px-1 text-gray-700"
                      :disabled="is24HourSelected(day)"
                      @click="addTimeSlot(day)"
                    >
                      <PlusIcon class="h-4 w-4" />
                    </UIButton>
                    
                    <!-- Delete slot button (on every row) -->
                    <UIButton
                      :text="true"
                      :aria-label="$t('settings.availability.schedule.remove_availability_hour')"
                      class="px-1 text-gray-700"
                      @click="removeTimeSlot(day, intervalIndex)"
                    >
                      <Trash01Icon class="h-4 w-4" />
                    </UIButton>
                    
                    <!-- Copy button with popover (only on first row) -->
                    <div class="relative">
                      <UIPopover
                        v-if="intervalIndex === 0"
                        trigger="click"
                        :show="activeHourIndexForCopy === index"
                        class="!rounded-md !pb-0"
                        @clickoutside="closeCopyPopover"
                      >
                        <template #trigger>
                          <UIButton
                            :text="true"
                            :aria-label="$t('settings.availability.schedule.copy_availability_hour')"
                            class="mt-2.5 px-1 text-gray-700"
                            @click="toggleCopyPopover(index)"
                          >
                            <Copy06Icon class="h-4 w-4" />
                          </UIButton>
                        </template>
                        <div class="w-40">
                          <p class="text-md mt-0 px-0.5 font-normal text-gray-600">
                            {{ $t('calendar_advanced.availability.copy_times_to') }}...
                          </p>
                          <UICheckbox
                            id="copy-all-days"
                            name="All Days"
                            :checked="copyToDays.length === 7"
                            class="text-md flex w-20 p-2"
                            @change="toggleAllDays"
                          >
                            <div class="font-md mr-32 w-full font-semibold text-gray-700">
                              {{ $t('calendar_advanced.availability.copy_to_all') }}
                            </div>
                          </UICheckbox>
                          <div
                            v-for="(w_day, k) in OFFICE_DAYS"
                            :key="k"
                            class="flex items-center p-2"
                          >
                            <UICheckbox
                              :id="`copy-${w_day}`"
                              :name="w_day"
                              :checked="copyToDays.includes(w_day)"
                              :disabled="w_day === day.day"
                              class="text-md flex w-20"
                              @change="(checked: boolean) => toggleCopyDay(w_day, checked)"
                            >
                              <div class="font-md mr-32 w-full font-semibold capitalize text-gray-700">
                                {{ w_day }}
                              </div>
                            </UICheckbox>
                          </div>
                          <UIButton
                            type="primary"
                            size="small"
                            class="my-2 w-full text-center"
                            @click="applyCopyToDays(day)"
                          >
                            {{ $t('calendar_advanced.availability.apply') }}
                          </UIButton>
                        </div>
                      </UIPopover>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Unavailable text -->
              <span v-else class="text-sm text-gray-400 flex-1 py-0.5">
                {{ $t('settings.availability.schedule.unavailable') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </UIModalContent>

    <template #footer>
      <UIModalFooter
        id="bulk-edit-weekly-modal-footer"
        :positive-text="$t('settings.availability.schedule.save_weekly_hours')"
        :negative-text="$t('settings.calendar.cancel')"
        :loading="props.isSaving"
        :disabled="props.isSaving"
        @positive-click="handleSave"
        @negative-click="handleCancel"
      />
    </template>
  </UIModal>
</template>

<script setup lang="ts">
import { computedTimezones } from '@/utils/timezonesOptions'
import { ChevronDownIcon, ChevronUpIcon, Copy06Icon, PlusIcon, Trash01Icon, XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'
import {
  UIAvatar,
  UIButton,
  UICheckbox,
  UIFormItem,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UIPopover,
  UISelect,
  UITimePicker,
} from '@gohighlevel/ghl-ui'
import { computed, PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { DayAvailability } from './DayAvailability'

interface BulkEditUser {
  id: string
  name: string
  profilePhoto?: string
}

interface TimeInterval {
  from: string
  to: string
}

interface WeeklyDay {
  day: string
  isActive: boolean
  intervals: TimeInterval[]
}

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  users: {
    type: Array as PropType<BulkEditUser[]>,
    required: true,
  },
  initialTimezone: {
    type: String,
    default: '',
  },
  initialWeeklySchedule: {
    type: Array as PropType<DayAvailability[]>,
    default: () => [],
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'save', data: { timezone: string; weeklySchedule: WeeklyDay[] }): void
  (e: 'cancel'): void
}>()

const { t } = useI18n()

const internalShow = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value),
})

const selectedTimezone = ref(props.initialTimezone)
const weeklySchedule = ref<WeeklyDay[]>([])

// Copy to days functionality
const OFFICE_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const copyToDays = ref<string[]>([])
const activeHourIndexForCopy = ref(-1)

// User list expansion state for "Assign to" section
const isUserListExpanded = ref(false)
const MAX_VISIBLE_USERS = 6

// Visible users (first 6) and remaining count for truncated display
const visibleUsers = computed(() => {
  if (isUserListExpanded.value) {
    return props.users
  }
  return props.users.slice(0, MAX_VISIBLE_USERS)
})

const remainingUsersCount = computed(() => {
  return Math.max(0, props.users.length - MAX_VISIBLE_USERS)
})

const hasMoreUsers = computed(() => {
  return props.users.length > MAX_VISIBLE_USERS
})

// Initialize weekly schedule with default availability when modal opens
// For bulk edit, we always start fresh with default Mon-Fri 8AM-5PM schedule
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      selectedTimezone.value = props.initialTimezone
      // Always use default schedule for bulk edit (Mon-Fri 8AM-5PM)
      weeklySchedule.value = createDefaultWeeklySchedule()
      // Reset user list expansion state
      isUserListExpanded.value = false
    }
  },
  { immediate: true }
)

function createDefaultWeeklySchedule(): WeeklyDay[] {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  return days.map((day, index) => ({
    day,
    isActive: index >= 1 && index <= 5, // Mon-Fri active by default
    intervals: index >= 1 && index <= 5 ? [{ from: '8:0', to: '17:0' }] : [],
  }))
}

function capitalizeAbbr(day: string): string {
  return day.charAt(0).toUpperCase() + day.slice(1, 3)
}

function getInitials(name: string): string {
  if (!name) return ''
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function toggleDayEnabled(day: WeeklyDay) {
  day.isActive = !day.isActive
  if (day.isActive && day.intervals.length === 0) {
    day.intervals.push({ from: '8:0', to: '17:0' })
  }
}

function is24HourSelected(day: WeeklyDay): boolean {
  // Match existing pattern: 0:0 to 0:0 means 24-hour (midnight to midnight)
  return day.intervals.length === 1 && day.intervals[0].from === '0:0' && day.intervals[0].to === '0:0'
}

function addTimeSlot(day: WeeklyDay) {
  const lastSlot = day.intervals[day.intervals.length - 1]
  if (lastSlot) {
    const [h] = lastSlot.to.split(':').map(Number)
    const newFrom = `${h + 1}:0`
    const newTo = `${Math.min(h + 2, 23)}:0`
    day.intervals.push({ from: newFrom, to: newTo })
  } else {
    day.intervals.push({ from: '9:0', to: '17:0' })
  }
}

function closeCopyPopover() {
  copyToDays.value = []
  activeHourIndexForCopy.value = -1
}

function toggleCopyPopover(index: number) {
  if (activeHourIndexForCopy.value === index) {
    activeHourIndexForCopy.value = -1
  } else {
    activeHourIndexForCopy.value = index
  }
}

function toggleAllDays(checked: boolean) {
  if (checked) {
    copyToDays.value = [...OFFICE_DAYS]
  } else {
    copyToDays.value = []
  }
}

function toggleCopyDay(day: string, checked: boolean) {
  if (checked) {
    if (!copyToDays.value.includes(day)) {
      copyToDays.value.push(day)
    }
  } else {
    const idx = copyToDays.value.indexOf(day)
    if (idx > -1) {
      copyToDays.value.splice(idx, 1)
    }
  }
}

function applyCopyToDays(sourceDay: WeeklyDay) {
  // Copy the source day's intervals to all selected days
  const sourceIntervals = sourceDay.intervals.map(i => ({ from: i.from, to: i.to }))
  
  copyToDays.value.forEach(targetDayName => {
    const targetDay = weeklySchedule.value.find(d => d.day === targetDayName)
    if (targetDay && targetDay.day !== sourceDay.day) {
      targetDay.isActive = true
      targetDay.intervals = sourceIntervals.map(i => ({ from: i.from, to: i.to }))
    }
  })
  
  closeCopyPopover()
}

function removeTimeSlot(day: WeeklyDay, index: number) {
  if (day.intervals.length === 1) {
    toggleDayEnabled(day)
  } else {
    day.intervals.splice(index, 1)
  }
}

function handleSave() {
  emit('save', {
    timezone: selectedTimezone.value,
    weeklySchedule: weeklySchedule.value,
  })
}

function handleCancel() {
  emit('cancel')
  internalShow.value = false
}
</script>
